import React, { useState, useRef, useEffect } from 'react';
import { Bell, Sparkles, CheckCircle2, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getProfileCompleteness } from '@/lib/profileCompleteness';

interface NotificationPopoverProps {
    onOpenCompleteProfile: () => void;
    align?: 'left' | 'right';
    placement?: 'top' | 'bottom';
    isMobile?: boolean;
}

export const NotificationPopover: React.FC<NotificationPopoverProps> = ({ 
    onOpenCompleteProfile, 
    align = 'right',
    placement = 'bottom',
    isMobile = false
}) => {
    const { profile } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const popoverRef = useRef<HTMLDivElement>(null);

    // Avalia todos os 7 campos do perfil
    const { percent, isComplete, missingCount } = getProfileCompleteness(profile);

    const pendingCount = !isComplete ? 1 : 0;

    // Fechar ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleActionClick = () => {
        setIsOpen(false);
        onOpenCompleteProfile();
    };

    return (
        <div className="relative inline-block" ref={popoverRef}>
            {/* Botão do Sino com Badge */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`relative p-1.5 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    isMobile 
                        ? 'p-2 text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg' 
                        : 'text-slate-400 hover:text-white hover:bg-slate-700'
                }`}
                aria-label="Notificações"
                title="Notificações"
            >
                <Bell size={isMobile ? 20 : 16} />
                
                {/* Badge Laranja / Âmbar Animada */}
                {pendingCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center pointer-events-none">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                        <span className="relative inline-flex items-center justify-center rounded-full h-4 w-4 bg-amber-500 text-[10px] font-bold text-white shadow-sm ring-1 ring-slate-900">
                            {pendingCount}
                        </span>
                    </span>
                )}
            </button>

            {/* Popover Dropdown */}
            {isOpen && (
                <div 
                    className={`absolute w-80 sm:w-84 bg-white border border-slate-200/80 rounded-2xl shadow-2xl z-50 animate-in fade-in zoom-in-95 duration-200 overflow-hidden text-slate-800 ${
                        placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
                    } ${
                        align === 'right' ? 'right-0' : 'left-0'
                    }`}
                >
                    {/* Header do Popover */}
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Bell size={16} className="text-slate-700" />
                            <span className="text-sm font-bold text-slate-800">Notificações</span>
                        </div>
                        {pendingCount > 0 ? (
                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                                1 pendente
                            </span>
                        ) : (
                            <span className="text-[11px] font-medium text-slate-400">
                                0 pendentes
                            </span>
                        )}
                    </div>

                    {/* Conteúdo das Notificações */}
                    <div className="p-3">
                        {!isComplete ? (
                            <div className="p-3.5 bg-gradient-to-br from-indigo-50/80 via-violet-50/40 to-indigo-50/80 border border-indigo-100/90 rounded-xl space-y-3 transition-all">
                                <div className="flex items-start gap-2.5">
                                    <div className="p-2 bg-indigo-600 text-white rounded-lg shadow-sm shrink-0 mt-0.5">
                                        <Sparkles size={16} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="text-xs font-bold text-slate-900 leading-tight">
                                            Quase lá! Personalize seu espaço
                                        </h4>
                                        <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                                            {missingCount === 1 
                                                ? 'Falta apenas 1 item para seu perfil ficar 100% completo!'
                                                : `Faltam ${missingCount} itens para seu perfil ficar 100% completo!`
                                            }
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-1 pt-0.5">
                                    <div className="flex justify-between text-[10px] font-semibold text-indigo-900">
                                        <span>Progresso do Perfil</span>
                                        <span>{percent}%</span>
                                    </div>
                                    <div className="w-full bg-indigo-100 rounded-full h-1.5 overflow-hidden">
                                        <div 
                                            className="bg-indigo-600 h-1.5 rounded-full transition-all duration-500"
                                            style={{ width: `${percent}%` }}
                                        />
                                    </div>
                                </div>

                                <button
                                    onClick={handleActionClick}
                                    className="w-full flex items-center justify-center gap-2 py-2 px-3 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white text-xs font-bold rounded-lg shadow-sm shadow-indigo-200 transition-all"
                                >
                                    Completar Perfil
                                    <ArrowRight size={14} />
                                </button>
                            </div>
                        ) : (
                            <div className="py-6 px-4 text-center space-y-2">
                                <div className="w-10 h-10 mx-auto rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-100">
                                    <CheckCircle2 size={20} />
                                </div>
                                <h4 className="text-xs font-bold text-slate-800">Perfil 100% completo!</h4>
                                <p className="text-xs text-slate-500 max-w-[220px] mx-auto">
                                    Todas as suas informações e personalizações estão em dia.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
